import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchUserProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      if (session?.user) {
        await fetchUserProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (authUser) => {
    try {
      console.log('Fetching profile for user:', authUser.id);
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          companies (
            id,
            name,
            phone,
            address,
            city
          )
        `)
        .eq('user_id', authUser.id)
        .single();

      console.log('Profile fetch result:', { profile, error });

      if (error && error.code !== 'PGRST116') {
        console.error('Profile fetch error:', error);
        setUser({ ...authUser, profile: null });
      } else {
        setUser({ ...authUser, profile: profile || null });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUser({ ...authUser, profile: null });
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      console.log('Attempting login for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      console.log('Login result:', { data, error });
      
      if (error) {
        let errorMessage = 'Prihlásenie zlyhalo';
        
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Nesprávny email alebo heslo';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Prosím potvrďte svoj email pred prihlásením. Skontrolujte inbox aj spam.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Príliš veľa pokusov. Skúste to neskôr.';
        } else {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }
      
      if (data.user && !data.user.email_confirmed_at) {
        setLoading(false);
        return {
          success: false,
          error: 'Prosím potvrďte svoj email pred prihlásením. Skontrolujte inbox aj spam.'
        };
      }
      
      await fetchUserProfile(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      return { 
        success: false, 
        error: error.message || 'Prihlásenie zlyhalo' 
      };
    }
  };

  const register = async (companyName, adminName, email, password) => {
    try {
      setLoading(true);
      console.log('Starting registration for:', { companyName, adminName, email });
      
      // Kontrola či email už existuje
      const { data: existingUser } = await supabase.auth.getUser();
      if (existingUser?.user?.email === email) {
        return {
          success: false,
          error: 'Používateľ s týmto emailom už existuje'
        };
      }
      
      // First create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: adminName
          }
        }
      });
      
      console.log('Auth signup result:', { authData, authError });
      
      if (authError) {
        let errorMessage = 'Registrácia zlyhala';
        
        if (authError.message.includes('User already registered')) {
          errorMessage = 'Používateľ s týmto emailom už existuje';
        } else if (authError.message.includes('Password should be at least')) {
          errorMessage = 'Heslo musí mať aspoň 6 znakov';
        } else if (authError.message.includes('Unable to validate email address')) {
          errorMessage = 'Nesprávny formát emailovej adresy';
        } else {
          errorMessage = authError.message;
        }
        
        throw new Error(errorMessage);
      }
      
      if (authData.user) {
        console.log('User created, creating company...');
        
        // Create company
        const { data: company, error: companyError } = await supabase
          .from('companies')
          .insert({
            name: companyName,
            admin_user_id: authData.user.id,
            created_at: new Date().toISOString()
          })
          .select()
          .single();
        
        console.log('Company creation result:', { company, companyError });
        
        if (companyError) {
          console.error('Company creation failed:', companyError);
          // Ak sa nepodarí vytvoriť firmu, pokúsime sa vymazať používateľa
          await supabase.auth.admin.deleteUser(authData.user.id);
          throw new Error('Chyba pri vytváraní firmy: ' + companyError.message);
        }
        
        // Create user profile
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: authData.user.id,
            email: email,
            full_name: adminName,
            role: 'ADMIN',
            company_id: company.id,
            created_at: new Date().toISOString()
          });
        
        console.log('Profile creation result:', { profileError });
        
        if (profileError) {
          console.error('Profile creation failed:', profileError);
          throw new Error('Chyba pri vytváraní profilu: ' + profileError.message);
        }
        
        const message = authData.user.email_confirmed_at 
          ? 'Registrácia úspešná! Môžete sa prihlásiť.'
          : 'Registrácia úspešná! Skontrolujte email pre potvrdenie účtu.';
        
        setLoading(false);
        
        return { 
          success: true, 
          user: authData.user, 
          company: company,
          message: message
        };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      setLoading(false);
      return { 
        success: false, 
        error: error.message || 'Registrácia zlyhala' 
      };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      return { success: true, message: 'Email na reset hesla bol odoslaný' };
    } catch (error) {
      return { success: false, error: error.message || 'Chyba pri resetovaní hesla' };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return { success: true, message: 'Heslo bolo úspešne zmenené' };
    } catch (error) {
      return { success: false, error: error.message || 'Chyba pri zmene hesla' };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    resetPassword,
    updatePassword,
    isAuthenticated: !!user,
    isAdmin: user?.profile?.role === 'ADMIN',
    isDriver: user?.profile?.role === 'DRIVER',
    supabase
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 